/**
 * @file finance-order-report.tsx
 * @module app/modules/finance-reports/finance-order-report
 * @description Displays finance order-report data with status presentation, filters, and pagination.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Card, Col, FormLabel, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useEffect, useState } from "react";
import {
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  PermissionModule,
  Piece,
  Download,
} from "../../../utils/constants";
import APICallService from "../../../api/apiCallService";
import { seller } from "../../../api/apiEndPoints";
import Method from "../../../utils/methods";
import Loader from "../../../Global/loader";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { useAuth } from "../auth";
import clsx from "clsx";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { useWarehouse } from "../../context/WarehouseContext";
import React from "react";
import { STATUS_UI_MAP } from "../../../utils/common.constants";
import "./switch-toggle.css";
import { getQuantityTypeLabel } from "../../../utils";
import { ConsolidateFinanceOrders, FinanceOrderValues } from "./finance-report-metrics";
import { FinanceReportResponseCache } from "./finance-sales-report-cache";
import ImageWithFallback from "../_common/components/image-with-fallback";

const getStatusUI = (status: any) => {
  const statusCode = Number(status);
  return (
    STATUS_UI_MAP[statusCode] || {
      color: "#6C757D",
      badgeClass: "bg-light text-gray-700",
      label: "Unknown",
    }
  );
};

const STATUS_FILTER_OPTIONS = Object.keys(STATUS_UI_MAP).map((statusKey) => {
  const statusCode = Number(statusKey);
  const statusUI = STATUS_UI_MAP[statusCode];
  return {
    label: statusUI.label,
    value: statusCode,
  };
});

const FinanceOrderReport = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const [endDate, setEndDate] = useState<any>(new Date());
  const [startDate, setStartDate] = useState<any>(new Date());
  const [fetchLoading, setFetchLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<any>();
  const [selectedDistrict, setSelectedDistrict] = useState<any>();
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [selectedStatus, setSelectedStatus] = useState<any>(null);
  const [productsList, setProductsList] = useState<any>([]);
  const [isExporting, setIsExporting] = useState(false);
  const savedData = localStorage.getItem("orderReport-viewMode") as
    | "normal"
    | "consolidated";
  const [viewMode, setViewMode] = useState<"normal" | "consolidated">(
    savedData ?? "normal",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const exportRef = React.useRef<number>(0);
  const requestGeneration = React.useRef(0);
  const responseCache = React.useRef(new FinanceReportResponseCache());
  useEffect(() => { responseCache.current.clear(); }, [currentUser]);
  const productOptionsCache = React.useRef<{ scope: string; rows: FinanceOrderValues[] } | null>(null);
  const [loadedScope, setLoadedScope] = useState("");
  const [reportError, setReportError] = useState(false);
  const currentScope = JSON.stringify([
    selectedWarehouse?.value || "",
    startDate ? Method.convertDateToFormat(startDate, "YYYY-MM-DD") : "",
    endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
    selectedProduct?.value || "",
  ]);
  const reportReady = loadedScope === currentScope && !fetchLoading && !reportError;
  const isAllowedToDownload = Method.hasPermission(
    PermissionModule.FinanceOrderReportPermission,
    Download,
    currentUser,
  );

  useEffect(() => {
    (async () => {
      if (
        !Method.hasModulePermission(
          PermissionModule.FinanceOrderReportPermission,
          currentUser,
        )
      ) {
        return window.history.back();
      }
      setSelectedBrand(null);
      setSelectedProduct(null);
      setSelectedDistrict(null);
      await fetchOrder(
        startDate,
        endDate,
        null,
        null,
      );
    })();
    return () => { requestGeneration.current++; };
  }, [selectedWarehouse?.value]);

  const fetchOrder = async (
    startDate: string,
    endDate: string,
    brands?: any,
    product?: any,
    district?: any,
  ) => {
    const generation = ++requestGeneration.current;
    setFetchLoading(true);
    setProductLoading(true);
    setReportError(false);
    setLoadedScope("");
    setPaymentData([]);
    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
      utcOffset: -180,
    };
    if (brands) {
      params = {
        ...params,
        ["brands[0]"]: brands,
      };
    }
    if (district) {
      params = {
        ...params,
        ["districts[0]"]: district,
      };
    }
    if (product) {
      params = {
        ...params,
        variant: product,
      };
    }
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    const scope = JSON.stringify([params.warehouse || "", params.fromDate, params.toDate, product || ""]);
    const optionScope = JSON.stringify([params.warehouse || "", params.fromDate, params.toDate]);
    if (productOptionsCache.current?.scope !== optionScope) setProductsList([]);
    try {
      if (!params.warehouse || !params.fromDate || !params.toDate || params.fromDate > params.toDate) throw new Error("Invalid report scope");
      const apiService = new APICallService(
        seller.orderReport, params, "", "", "", "",
        PermissionModule.FinanceOrderReportPermission, params.warehouse,
      );
      const response = await responseCache.current.load(JSON.stringify(params), () => apiService.callAPI(), Array.isArray);
      if (generation !== requestGeneration.current) return;
      if (!Array.isArray(response)) throw new Error("Report request failed");
      let optionRows = response;
      if (product) {
        if (productOptionsCache.current?.scope === optionScope) optionRows = productOptionsCache.current.rows;
        else {
          const optionParams = { ...params };
          delete optionParams.variant;
          const optionsApi = new APICallService(
            seller.orderReport, optionParams, "", "", "", "",
            PermissionModule.FinanceOrderReportPermission, params.warehouse,
          );
          optionRows = await responseCache.current.load(JSON.stringify(optionParams), () => optionsApi.callAPI(), Array.isArray);
          if (generation !== requestGeneration.current) return;
          if (!Array.isArray(optionRows)) throw new Error("Product options request failed");
        }
      }
      productOptionsCache.current = { scope: optionScope, rows: optionRows.map((row: FinanceOrderValues) => ({ variant: row.variant })) };
      setPaymentData(response);
      setLoadedScope(scope);
      getUniqueProducts(optionRows);
      getUniqueBrands(response);
      getUniqueDistricts(response);
    } catch {
      if (generation === requestGeneration.current) {
        setPaymentData([]);
        setLoadedScope("");
        setReportError(true);
      }
    } finally {
      if (generation === requestGeneration.current) {
        setFetchLoading(false);
        setProductLoading(false);
      }
    }
  };

  const getUniqueProducts = (data: any) => {
    const uniqueVariants = new Set();
    data.forEach((item: any) => {
      const variant = item?.variant;
      const variantInfo = {
        title: variant?.title,
        id: variant?._id,
        media: variant?.media,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant),
    );
    setProductsList(uniqueVariantsArray);
  };

  const getUniqueBrands = (data: any) => {
    const uniqueVariants = new Set();
    data.forEach((item: any) => {
      const variant = item?.variant?.product?.brand;
      const variantInfo = {
        title: variant?.title,
        id: variant?.reference,
        media: variant?.media,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant),
    );
  };

  const getUniqueDistricts = (data: any) => {
    const uniqueVariants = new Set();
    data?.forEach((item: any) => {
      const variant = item?.district;
      const variantInfo = {
        title: variant?.name,
        id: variant?.id,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant),
    );
  };

  const handleChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate && endDate) {
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        selectedProduct ? selectedProduct?.value : null,
        selectedDistrict ? selectedDistrict?.value : null,
      );
    } else {
      requestGeneration.current++;
      setPaymentData([]);
      setLoadedScope("");
      setFetchLoading(false);
      setProductLoading(false);
    }
  };

  const handleProductSelect = async (event: any) => {
    if (event) {
      setPaymentData([]);
      setSelectedProduct(event);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        event.value,
        selectedDistrict ? selectedDistrict?.id : null,
      );
    } else {
      setPaymentData([]);
      setSelectedProduct(null);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        null,
        selectedDistrict ? selectedDistrict?.id : null,
      );
    }
  };

  const onMenuClose = async () => {
    // setSearch("");
  };
  // --- Summary Calculations ---
  const scopedPaymentData = loadedScope === currentScope ? paymentData : [];
  const filteredPaymentData = selectedStatus
    ? scopedPaymentData.filter(
        (val: any) => Number(val?.status) === Number(selectedStatus?.value),
      )
    : scopedPaymentData;

  const netInclVAT = filteredPaymentData.reduce(
    (acc: number, val: any) =>
      acc +
      (val?.totalAmount || 0) -
      (val?.totalReturnedQuantities || 0) * (val?.averagePrice || 0),
    0,
  );
  const netExclVAT = filteredPaymentData.reduce(
    (acc: number, val: any) =>
      acc +
      (val?.totalTaxAmount || 0) -
      (val?.totalReturnedQuantities || 0) * (val?.averageTaxPrice || 0),
    0,
  );
  const netVAT = filteredPaymentData.reduce(
    (acc: number, val: any) =>
      acc +
      (val?.tax || 0) -
      (val?.totalReturnedQuantities || 0) * (val?.taxPerUnit || 0),
    0,
  );

  // Calculate quantities by type
  const qtyByType = filteredPaymentData.reduce(
    (acc: any, val: any) => {
      const qty = val?.totalQuantities || 0;
      if (val?.quantityType === CartonWithDozens) {
        acc.cartonWithDozens += qty;
      } else if (val?.quantityType === CartonWithPieces) {
        acc.cartonWithPieces += qty;
      } else if (val?.quantityType === Dozen) {
        acc.dozen += qty;
      } else if (val?.quantityType === Piece) {
        acc.piece += qty;
      }
      return acc;
    },
    { cartonWithDozens: 0, cartonWithPieces: 0, dozen: 0, piece: 0 },
  );

  // Consolidate orders by product variant and quantity type
  const consolidatedData = ConsolidateFinanceOrders(filteredPaymentData);

  const displayData =
    viewMode === "consolidated" ? consolidatedData : filteredPaymentData;

  // Sort displayData by total quantity
  const sortedDisplayData = [...displayData].sort((a, b) => {
    const qtyA = a?.totalQuantities || 0;
    const qtyB = b?.totalQuantities || 0;

    if (sortOrder === "asc") {
      return qtyA - qtyB;
    } else if (sortOrder === "desc") {
      return qtyB - qtyA;
    }
    return 0;
  });

  const toggleSort = () => {
    if (sortOrder === null) {
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortOrder(null);
    }
  };

  const exportHeaders = [
    "Product Name",
    "Reference Key",
    "Customer Name",
    "Quantity Type",
    `${viewMode === "consolidated" ? "Average Unit Price" : "Unit Price"} (Incl. VAT)`,
    `${viewMode === "consolidated" ? "Average Unit Price" : "Unit Price"} (Excl. VAT)`,
    "VAT / Unit",
    "Ordered Quantity",
    "Order Value (Incl. VAT)",
    "Order Value (Excl. VAT)",
    "Order VAT Amount",
  ];

  const rowMapper = (val: any, isConsolidated: boolean) => {
    const totalQty = val?.totalQuantities || 0;
    const returnedQty = val?.totalReturnedQuantities || 0;

    const deliveredTotalIncl = val?.totalAmount || 0;
    const deliveredTotalExcl = val?.totalTaxAmount || 0;
    const deliveredVAT = val?.tax || 0;

    const returnedTotalIncl = returnedQty * (val?.averagePrice || 0);
    const returnedTotalExcl = returnedQty * (val?.averageTaxPrice || 0);
    const returnedVAT = returnedQty * (val?.taxPerUnit || 0);

    const netIncl = deliveredTotalIncl - returnedTotalIncl;
    const netExcl = deliveredTotalExcl - returnedTotalExcl;
    const netVAT = deliveredVAT - returnedVAT;

    return [
      val?.variant?.title || "N/A",
      ...(isConsolidated
        ? []
        : [val?.refKey || "N/A", val?.customerName || "N/A"]),
      getQuantityTypeLabel(val?.quantityType),
      val?.averagePrice || 0,
      val?.averageTaxPrice || 0,
      val?.taxPerUnit || 0,
      totalQty,
      netIncl,
      netExcl,
      netVAT,
    ];
  };

  const handleExportExcel = async () => {
    if (!reportReady || !paymentData.length) return;
    const runId = ++exportRef.current;
    const toastId = toast.loading("Preparing export...");

    try {
      setIsExporting(true);
      if (runId !== exportRef.current) {
        toast.dismiss(toastId);
        return;
      }

      let data = [...paymentData];

      // Filter by selected status if applicable
      if (selectedStatus) {
        data = data.filter(
          (val: any) => Number(val?.status) === Number(selectedStatus?.value),
        );
      }

      // Use consolidated data if in consolidated view
      if (viewMode === "consolidated") {
        data = ConsolidateFinanceOrders(data);
      }

      if (data.length === 0) {
        toast.dismiss(toastId);
        toast.error("No records found to export");
        return;
      }

      const workbook = XLSX.utils.book_new();
      let headers = exportHeaders;
      if (viewMode === "consolidated") {
        headers = exportHeaders.filter(
          (header) => !["Reference Key", "Customer Name"].includes(header),
        );
      }
      const worksheetData: any[] = [headers];

      toast.update(toastId, {
        render: `Mapping ${data.length} records...`,
        type: "info",
        isLoading: true,
      });
      worksheetData.push(
        ...data.map((val: any) => rowMapper(val, viewMode === "consolidated")),
      );

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet["!cols"] = [
        { width: 35 },
        { width: 15 },
        { width: 20 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
      ];
      if (viewMode === "consolidated") {
        worksheet["!cols"].splice(1, 2);
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders Report");
      XLSX.writeFile(
        workbook,
        `Finance-Orders-Report-${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      toast.update(toastId, {
        render: "Export completed successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeButton: true,
        progress: undefined,
      });
    } catch (error) {
      if (runId === exportRef.current) {
        toast.dismiss(toastId);
        toast.error("Export failed: " + (error as Error).message);
      }
    } finally {
      if (runId === exportRef.current) setIsExporting(false);
    }
  };

  return (
    <div className="p-5">
      {reportError && <div className="alert alert-danger" role="alert">Report data could not be loaded.</div>}
      {/* Header Row */}
      <Row className="g-4">
        <Col>
          <div className="d-flex align-items-center mt-4">
            <h1
              className="fs-22 fw-bolder m-0"
              style={{ cursor: "pointer" }}
              title="This report shows the original order placed at the time of order and it consist every type of order : Placed,Processed,Delivered,Cancelled"
            >
              Order Report
            </h1>
            <button
              type="button"
              className="uw-btn uw-btn-light uw-btn-icon btn btn-icon btn-light ms-3"
              aria-label="Refresh report"
              title="Refresh report"
              disabled={!reportReady || isExporting}
              onClick={() => {
                responseCache.current.clear();
                productOptionsCache.current = null;
                void fetchOrder(
                  Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
                  Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
                  selectedBrand?.id || null,
                  selectedProduct?.value || null,
                  selectedDistrict?.value || null,
                );
              }}
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            </button>
            {fetchLoading && (
              <span
                className="d-inline-flex align-items-center ms-4 fs-14 fw-semibold text-primary"
                role="status"
                aria-live="polite"
              >
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
                Loading report data...
              </span>
            )}
          </div>
        </Col>
      </Row>

      {/* Filters Row */}
      <Row className="g-4 gx-lg-5 align-items-end mt-1 mb-4">
        <Col xs={12} sm={6} md="auto">
          <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
            Filter by Dates
          </FormLabel>
          <div className="min-w-md-288px">
            <CustomDatePicker
              className="form-control bg-white min-h-60px fs-16 fw-bold text-dark min-w-md-288px min-w-175px cursor-pointer"
              onChange={handleChange}
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              dateFormat="dd/MM/yyyy"
              showFullMonthYearPicker={true}
              maxDate={new Date()}
              disabled={fetchLoading}
              inputTextBG="bg-white"
            />
          </div>
        </Col>

        <Col xs={12} sm={6} md="auto" style={{ width: "300px" }}>
          <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
            Filter by Product
          </FormLabel>
          <div className="w-100">
            <CustomSelect
              backgroundColor="#ffff"
              isClearable={true}
              minWidth="100%"
              minHeight="60px"
              controlFontSize="15px"
              optionFontSize="14px"
              placeholder="Select Product"
              options={
                productsList && productsList?.length
                  ? productsList?.map((val: any) => {
                      return {
                        label: (
                          <div className="d-flex align-items-center">
                            <div className="symbol symbol-30px border me-3">
                              <ImageWithFallback
                                src={val?.media?.[0]?.url}
                                className="object-fit-contain"
                                alt=""
                                noDefaultSize
                              />
                            </div>
                            <span className="fs-14 fw-600 text-black mb-0">
                              {val?.title}
                            </span>
                          </div>
                        ),
                        value: val?.id,
                        id: val?.id,
                        variant: val,
                        title: val?.title,
                      };
                    })
                  : []
              }
              value={selectedProduct}
              onChange={(event: any) => handleProductSelect(event)}
              onMenuClose={() => onMenuClose()}
              disabled={productLoading || fetchLoading}
              isDisabled={productLoading || fetchLoading}
              isLoading={productLoading || fetchLoading}
              hideSelectedOptions={false}
            />
          </div>
        </Col>

        {viewMode !== "consolidated" && (
          <Col xs={12} sm={6} md="auto" style={{ width: "300px" }}>
            <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
              Filter by Status
            </FormLabel>
            <div className="w-100">
              <CustomSelect
                backgroundColor="#ffff"
                isClearable={true}
                minWidth="100%"
                minHeight="60px"
                controlFontSize="15px"
                optionFontSize="14px"
                placeholder="Select Status"
                options={STATUS_FILTER_OPTIONS}
                value={selectedStatus}
                onChange={(event: any) => setSelectedStatus(event || null)}
                disabled={fetchLoading}
                isDisabled={fetchLoading}
                hideSelectedOptions={false}
              />
            </div>
          </Col>
        )}

        {isAllowedToDownload && (
          <Col xs={12} md="auto" className="d-flex align-items-end ms-md-auto">
            <button
              className="uw-btn uw-btn-primary btn btn-primary fs-15 fw-bold px-6 min-h-60px d-inline-flex align-items-center"
              onClick={handleExportExcel}
              disabled={!reportReady || isExporting || !paymentData.length}
              data-analytics-event="finance_reports_finance_sales_report_download_clicked"
            >
              <i className="bi bi-file-earmark-excel fs-4 me-2"></i>
              {isExporting ? "Exporting..." : "Download Report"}
            </button>
          </Col>
        )}
      </Row>

      {/* Summary Cards */}
      <Row className="mt-5 g-5">
        <Col xl={12} lg={12}>
          <Card
            className={clsx(
              "border border-dashed bg-opacity-25 shadow-sm h-100",
              netInclVAT >= 0
                ? "border-success bg-light-success"
                : "border-danger bg-light-danger",
            )}
          >
            <Card.Header
              className={clsx(
                "min-h-50px px-6 py-3 border-bottom",
                netInclVAT >= 0
                  ? "border-success border-opacity-25"
                  : "border-danger border-opacity-25",
              )}
            >
              <div className="card-title m-0 d-flex align-items-center">
                <span
                  className={clsx(
                    "fs-15 fw-bolder text-uppercase",
                    netInclVAT >= 0 ? "text-success" : "text-danger",
                  )}
                >
                  Total Order Value
                </span>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-between flex-wrap align-items-center p-6 gap-3">
              <div className="d-flex flex-column flex-grow-1">
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  INCL VAT
                </span>
                <span className="fs-22 fw-bolder text-dark">
                  TSh {Method.getGeneralizedAmount(netInclVAT)}
                </span>
              </div>
              <div
                className={clsx(
                  "d-flex flex-column flex-grow-1 border-start ps-5",
                  netInclVAT >= 0
                    ? "border-success border-opacity-25"
                    : "border-danger border-opacity-25",
                )}
              >
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  EXCL VAT
                </span>
                <span className="fs-22 fw-bolder text-dark">
                  TSh {Method.getGeneralizedAmount(netExclVAT)}
                </span>
              </div>
              <div
                className={clsx(
                  "d-flex flex-column flex-grow-1 border-start ps-5",
                  netInclVAT >= 0
                    ? "border-success border-opacity-25"
                    : "border-danger border-opacity-25",
                )}
              >
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  VAT AMOUNT
                </span>
                <span
                  className={clsx(
                    "fs-22 fw-bolder",
                    netInclVAT >= 0 ? "text-success" : "text-danger",
                  )}
                >
                  TSh {Method.getGeneralizedAmount(netVAT)}
                </span>
              </div>
              {selectedProduct && (
                <div
                  className={clsx(
                    "d-flex flex-column flex-grow-1 border-start ps-5",
                    netInclVAT >= 0
                      ? "border-success border-opacity-25"
                      : "border-danger border-opacity-25",
                  )}
                >
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    TOTAL QTY
                  </span>
                  <span className="fs-22 fw-bolder text-dark">
                    {qtyByType.cartonWithDozens > 0 &&
                      `${qtyByType.cartonWithDozens}CWD `}
                    {qtyByType.cartonWithPieces > 0 &&
                      `${qtyByType.cartonWithPieces}CWP `}
                    {qtyByType.dozen > 0 && `${qtyByType.dozen}D `}
                    {qtyByType.piece > 0 && `${qtyByType.piece}P`}
                  </span>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table Section */}
      <Card className="border border-r10px mt-6">
        <Card.Header className="px-6 py-4 border-bottom border-gray-200 d-flex align-items-center">
          <h5 className="m-0">Order Details</h5>
          <h3 className="flex-grow-1 text-center m-0">
            {viewMode === "consolidated"
              ? "Product Specific View"
              : "Order Specific View"}
          </h3>
          <label className="switch-button ms-auto" htmlFor="viewModeToggle">
            <div className="switch-outer">
              <input
                id="viewModeToggle"
                type="checkbox"
                checked={viewMode === "consolidated"}
                onChange={(e) => {
                  localStorage.setItem(
                    "orderReport-viewMode",
                    e.target.checked ? "consolidated" : "normal",
                  );
                  setViewMode(e.target.checked ? "consolidated" : "normal");
                }}
              />
              <div className="button">
                <span className="button-toggle"></span>
                <span className="button-indicator"></span>
              </div>
            </div>
          </label>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
              <thead>
                {/* Visual Header Grouping */}
                <tr className="fs-13 fw-bolder text-uppercase bg-light text-gray-700 border-bottom border-gray-200">
                  <th className="min-w-250px p-4 border-end">
                    Product Summary
                  </th>
                  <th
                    colSpan={1}
                    className="text-center p-4 bg-light-primary  border-opacity-25"
                  >
                    Quantity
                  </th>
                  <th
                    colSpan={3}
                    className="text-center p-4 bg-light-info  border-opacity-25"
                  >
                    Unit Pricing
                  </th>
                  <th
                    colSpan={3}
                    className={clsx(
                      "text-center p-4 rounded-end",
                      netInclVAT >= 0
                        ? " bg-light-success "
                        : "bg-light-danger ",
                    )}
                  >
                    Order Value
                  </th>
                </tr>
                <tr className="fs-12 fw-bold h-50px align-middle text-muted bg-light bg-opacity-50">
                  <th className="min-w-250px border-end px-4">
                    PRODUCT NAME{" "}
                    {viewMode !== "consolidated" &&
                      "& ORDER ID - CUSTOMER NAME"}
                  </th>
                  <th
                    className="min-w-100px text-center border-start border-primary border-opacity-25 user-select-none"
                    style={{ cursor: "pointer" }}
                    onClick={toggleSort}
                  >
                    ORDERED QTY {sortOrder === "desc" && "↓"}{" "}
                    {sortOrder === "asc" && "↑"}
                  </th>
                  <th className="min-w-125px text-end">{viewMode === "consolidated" ? "AVG. INCL. VAT" : "INCL. VAT"}</th>
                  <th className="min-w-125px text-end">{viewMode === "consolidated" ? "AVG. EXCL. VAT" : "EXCL. VAT"}</th>
                  <th className="min-w-125px text-end border-end border-info border-opacity-25">
                    VAT / UNIT
                  </th>
                  <th className="min-w-150px text-end">ORDER INCL. VAT</th>
                  <th className="min-w-150px text-end">ORDER EXCL. VAT</th>
                  <th className="min-w-150px text-end opacity-75 pe-4">
                    ORDER VAT
                  </th>
                </tr>
              </thead>
              <tbody className="fs-14 fw-600">
                {fetchLoading ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="d-flex justify-content-center py-5">
                        <Loader loading={fetchLoading} />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {displayData?.length ? (
                      sortedDisplayData.map((val: any, index: number) => {
                        const statusUI = getStatusUI(val?.status);
                        const uom =
                          val?.quantityType === CartonWithDozens
                            ? "CWD"
                            : val?.quantityType === CartonWithPieces
                            ? "CWP"
                            : val?.quantityType === Dozen
                            ? "D"
                            : val?.quantityType === Piece
                            ? "P"
                            : "";
                        const totalQty = val?.totalQuantities || 0;
                        const returnedQty = val?.totalReturnedQuantities || 0;

                        const rowNetInclVAT =
                          (val?.totalAmount || 0) -
                          returnedQty * (val?.averagePrice || 0);
                        const rowNetExclVAT =
                          (val?.totalTaxAmount || 0) -
                          returnedQty * (val?.averageTaxPrice || 0);
                        const rowNetVAT =
                          (val?.tax || 0) -
                          returnedQty * (val?.taxPerUnit || 0);
                        const isPositive = rowNetInclVAT >= 0;

                        return (
                          <tr
                            key={index}
                            className="align-middle border-bottom border-gray-200 hover-bg-light transition"
                          >
                            {/* Product info */}
                            <td className="border-end px-4 py-3">
                              <div className="d-flex align-items-center">
                                <div className="symbol symbol-45px border me-3 bg-white rounded shadow-sm">
                                  <ImageWithFallback
                                    src={val?.variant?.media[0]?.url}
                                    className="object-fit-contain p-1 rounded"
                                    alt=""
                                    noDefaultSize
                                  />
                                </div>
                                <div className="d-flex flex-column text-nowrap">
                                  <span className="fs-14 fw-bolder text-dark mb-1">
                                    {val?.variant?.title?.replace(
                                      /\s*\)\s*/g,
                                      ")",
                                    )}
                                  </span>
                                  {viewMode === "normal" && (
                                    <>
                                      <span className="fs-12 fw-600 text-muted">
                                        #{val?.refKey} - {val?.customerName}
                                      </span>
                                      <span className="fs-12 fw-500 text-muted mt-1">
                                        <span
                                          className="rounded-circle me-2"
                                          aria-hidden="true"
                                          style={{
                                            width: "8px",
                                            height: "8px",
                                            backgroundColor: statusUI.color,
                                            display: "inline-block",
                                          }}
                                        />
                                        <span
                                          className="fw-700 me-2"
                                          style={{ color: statusUI.color }}
                                        >
                                          {statusUI.label}
                                        </span>
                                        <span className="visually-hidden">
                                          Order placed:
                                        </span>
                                        {val?.orderPlacedDate
                                          ? Method.convertDateToFormat(
                                              val?.orderPlacedDate,
                                              "DD/MM/YY [-] HH:mm",
                                            )
                                          : "N/A"}
                                      </span>
                                    </>
                                  )}
                                  {viewMode === "consolidated" && (
                                    <span className="fs-12 fw-600 text-muted">
                                      {val?.orderCount} order line
                                      {val?.orderCount > 1 ? "s" : ""}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Quantities */}
                            <td className="text-center font-monospace fw-600 border-start border-primary border-opacity-10 bg-light-primary bg-opacity-25">
                              {totalQty} <span className=" fs-12">{uom}</span>
                            </td>

                            {/* Unit Pricing */}
                            <td className="text-end fw-600 pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(val?.averagePrice)}
                            </td>
                            <td className="text-end fw-600 pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(
                                val?.averageTaxPrice,
                              )}
                            </td>
                            <td className="text-end fw-600 pe-3 border-end border-info border-opacity-10 text-nowrap bg-light-info bg-opacity-25">
                              <span className="text-info fs-11 me-1 opacity-75">
                                TSh{" "}
                                {Method.getGeneralizedAmount(val?.taxPerUnit)}
                              </span>
                            </td>

                            {/* Sales Totals */}
                            <td className="text-end fw-bolder text-dark pe-3 text-nowrap border-start border-opacity-10">
                              <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(rowNetInclVAT)}
                            </td>
                            <td className="text-end fw-bolder text-dark pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(rowNetExclVAT)}
                            </td>
                            <td
                              className={clsx(
                                "text-end fw-bolder pe-4 text-nowrap",
                                isPositive ? "text-success" : "text-danger",
                              )}
                            >
                              <span className="fs-11 me-1 fw-600 opacity-75">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(rowNetVAT)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={8}>
                          <div className="w-100 fs-15 py-8 fw-bold d-flex justify-content-center text-muted">
                            No Data found
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FinanceOrderReport;
